import qs from 'query-string'
import React, { createContext, useContext, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { useHistory } from 'react-router'
import ProductService from 'services/ProductService'
const queryParams = qs.parse(
  typeof window != 'undefined' ? window.location.search : ''
)

const getAttrFilters = () => {
  try {
    return JSON.parse(queryParams.filters)
  } catch {
    return {}
  }
}

const InnerPageContext = createContext()
const defaultSidebar = {
  min_price: queryParams.min_price,
  max_price: queryParams.max_price,
  attributes: {},
  categories: [],
  manufacturers: [],
  ordering: queryParams.ordering || '',
  loaded: false,
}
const defaultParams = {
  ordering: defaultSidebar.ordering,
  page: queryParams.page || 1,
  perPage: queryParams.perPage || 20,
  min_price: defaultSidebar.min_price || 0,
  max_price: defaultSidebar.max_price,
  attrs: getAttrFilters(),
}

export const InnerPageContextProvider = ({ children }) => {
  const [sidebar, SetSidebar] = useState({ ...defaultSidebar })
  const [productResolver, setProductResolver] = useState(null)
  const [onResolve, setOnResolve] = useState(null)
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [category, setCategory] = useState(true)
  const [params, setParams] = useState({ ...defaultParams })
  const [text, setText] = useState(null)
  const [additionalData, setAdditionalData] = useState({})
  const [disableAutoFilter, setDisableAutoFilter] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)
  const [previousCategoryId, setPreviousCategoryId] = useState(null)
  const history = useHistory()

  const setSidebar = newSidebar => {
    ReactDOM.unstable_batchedUpdates(() => {
      SetSidebar({ ...sidebar, ...newSidebar, loaded: true })
      setLoading(false)
    })
  }

  const setAttrs = (key)=>{
    setParams(old=>{
      // const newAttrs = {...old.attrs};
      const {[key]: _, ...rest} = old.attrs;
      // console.log(newAttrs)
      delete old.attrs.key
      return{...old, attrs:rest}
    }
    )
  }

  const setParam = (key, value, type = null) => {
    setParams(old => {
      if (key !== 'page' && params.page !== 1 && !initialLoad) {
        old.page = 1
      }
      if (type) {
        old[type] = {
          ...old[type],
          [key]: value,
        }
      } else old[key] = value
      return { ...old }
    })
  }

  useEffect(() => {
    ProductService.params = params
    const filters =
      params.attrs && Object.keys(params.attrs).length
        ? JSON.stringify(params.attrs)
        : ''

    if (shouldAppendParams()) {
      const searchParams = {}

      // Only add params if they have meaningful values
      if (params.min_price > 0) searchParams.min_price = params.min_price
      if (params.max_price) searchParams.max_price = params.max_price
      if (params.ordering) searchParams.ordering = params.ordering
      if (params.page > 1) searchParams.page = params.page
      if (params.perPage !== 20) searchParams.perPage = params.perPage
      if (filters) searchParams.filters = filters

      history.replace({
        pathname: window.location.pathname,
        search: qs.stringify(searchParams),
      })
    }

    // Mark that initial load is complete
    if (initialLoad) {
      setInitialLoad(false)
    }
    // eslint-disable-next-line
  }, [params])

  const onFilter = () => {
    if (!productResolver) return
    setLoading(true)
    productResolver()
      .then(data => {
        ReactDOM.unstable_batchedUpdates(() => {
          setLoading(false)
          if (data.sidebar) {
            setSidebar(data.sidebar)
            delete data.sidebar
          }
          setText(data.text)
          delete data.text
          if (data.products) {
            setProducts(data.products)
          }
          if (data.category) {
            setCategory(data.category)
          }
          if (typeof onResolve === 'function') {
            onResolve(data)
          }
        })
      })
      .catch(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (disableAutoFilter) return
    onFilter()
    // eslint-disable-next-line
  }, [productResolver, onResolve, params])

  const init = (options = {}) => {
    ReactDOM.unstable_batchedUpdates(() => {
      setCategory({})
      setLoading(true)
      SetSidebar({ ...defaultSidebar })
      setInitialLoad(true) // Reset initial load flag when initializing
      if (typeof options.resolver === 'function') {
        setProductResolver(() => options.resolver)
      }
      if (typeof options.onResolve === 'function') {
        setOnResolve(() => options.onResolve)
      }
    })
  }

  const clearFilters = () => {
    window.scrollTo(0, 0)
    setParams(old => ({
      ...old,
      ordering: defaultSidebar.ordering,
      page: 1,
      min_price: 0,
      max_price: undefined,
      attrs: {},
    }))
  }

  useEffect(() => {
    const currentCategoryId = category?.id || null

    if (
      previousCategoryId !== null &&
      currentCategoryId !== null &&
      currentCategoryId !== previousCategoryId
    ) {
      clearFilters()
    }

    if (currentCategoryId !== null) {
      setPreviousCategoryId(currentCategoryId)
    }
    // eslint-disable-next-line
  }, [category?.id])

  return (
    <InnerPageContext.Provider
      value={{
        sidebar: sidebar,
        params: params,
        loading: loading,
        productResolver: productResolver,
        category: category,
        products: products,
        text: text,
        additionalData,
        init: init,
        setAdditionalData,
        setSidebar: setSidebar,
        setParam: setParam,
        setAttrs: setAttrs,
        setCategory: setCategory,
        setLoading: setLoading,
        clearFilters: clearFilters,
        onFilter,
        setDisableAutoFilter,
      }}
    >
      {children}
    </InnerPageContext.Provider>
  )
}

const shouldAppendParams = () => {
  return (
    window.location.href.indexOf('/search') > -1 ||
    window.location.href.indexOf('/brand') > -1 ||
    window.location.href.indexOf('/category') > -1 ||
    window.location.href.indexOf('/promotion') > -1 ||
    window.location.href.indexOf('/promotion') > -1 ||
    window.location.href.indexOf('/sale') > -1
  )
}

const useInnerPage = () => useContext(InnerPageContext)

export default useInnerPage
